import { useLocation } from "react-router-dom";
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
} from "@react-google-maps/api";
import { useEffect, useState, useRef, useCallback } from "react";
import { DRIVER_IMAGE_URL, GOOGLE_MAPS_KEY } from "../api/Api.js";
import io from "socket.io-client";

const SOCKET_URL = DRIVER_IMAGE_URL;

const getDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371e3;
  const p1 = (lat1 * Math.PI) / 180;
  const p2 = (lat2 * Math.PI) / 180;
  const dp = ((lat2 - lat1) * Math.PI) / 180;
  const dl = ((lon2 - lon1) * Math.PI) / 180;
  const a =
    Math.sin(dp / 2) ** 2 +
    Math.cos(p1) * Math.cos(p2) * Math.sin(dl / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const mapStyles = [
  { elementType: "geometry", stylers: [{ color: "#f5f5f5" }] },
  { elementType: "labels.icon", stylers: [{ visibility: "off" }] },
  { elementType: "labels.text.fill", stylers: [{ color: "#616161" }] },
  { elementType: "labels.text.stroke", stylers: [{ color: "#f5f5f5" }] },
  { featureType: "road", elementType: "geometry", stylers: [{ color: "#ffffff" }] },
  { featureType: "road.arterial", elementType: "labels.text.fill", stylers: [{ color: "#757575" }] },
  { featureType: "road.highway", elementType: "geometry", stylers: [{ color: "#dadada" }] },
  { featureType: "water", elementType: "geometry", stylers: [{ color: "#c9c9c9" }] },
];

function LiveMapPage() {
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const cust_bookingid = params.get("cust_bookingid");

  const { isLoaded } = useJsApiLoader({ googleMapsApiKey: GOOGLE_MAPS_KEY });

  // --- DATA AND STATE REMAIN UNCHANGED ---
  const [socket, setSocket] = useState(null);
  const [currentPosition, setCurrentPosition] = useState(null);
  const [mapCenter, setMapCenter] = useState({ lat: 20.2961, lng: 85.8245 });
  const [drivenPath, setDrivenPath] = useState([]);
  const [plannedRoute, setPlannedRoute] = useState([]);
  const [heading, setHeading] = useState(0);
  const [status, setStatus] = useState("Connecting...");
  const [isOnline, setIsOnline] = useState(false);
  const [lastSeen, setLastSeen] = useState(null);
  const [speed, setSpeed] = useState(0);
  const [isFollowing, setIsFollowing] = useState(true);

  const mapRef = useRef(null);
  const offlineTimer = useRef(null);
  const isFollowingRef = useRef(true);

  const setFollow = (val) => {
    setIsFollowing(val);
    isFollowingRef.current = val;
  };

  useEffect(() => {
    if (currentPosition && isFollowing) {
      setMapCenter(currentPosition);
    }
  }, [currentPosition, isFollowing]);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    if (!cust_bookingid) return;

    const newSocket = io(SOCKET_URL);

    newSocket.on("connect", () => {
      setStatus("Connected ✅");
      newSocket.emit("admin_watch_trip", { enqueryId: cust_bookingid });
      newSocket.emit("get_admin_driver_location", cust_bookingid);
    });

    newSocket.on("connect_error", () => {
      setStatus("Connection failed ❌");
    });

    newSocket.on("admin_driver_location_response", (data) => {
      if (!Array.isArray(data) || data.length === 0) return;

      const formattedPath = data
        .map((item) => ({
          lat: Number(item.latitude),
          lng: Number(item.longitude),
        }))
        .filter((p) => !isNaN(p.lat) && !isNaN(p.lng));

      if (!formattedPath.length) return;

      setDrivenPath(formattedPath);
      const last = formattedPath[formattedPath.length - 1];
      setCurrentPosition(last);
      setLastSeen("From history");
    });

    newSocket.on("driver_location_update", (data) => {
      const newPos = { lat: Number(data.lat), lng: Number(data.lng) };

      setCurrentPosition(newPos);
      setHeading(Number(data.heading) || 0);
      setSpeed(data.speed ? (data.speed * 3.6).toFixed(1) : 0);
      setIsOnline(true);

      const timeStr = data.timestamp ? new Date(data.timestamp).toLocaleTimeString() : new Date().toLocaleTimeString();
      setLastSeen(timeStr);
      setStatus(`Live • ${timeStr}`);

      setDrivenPath((prev) => {
        if (!prev.length) return [newPos];
        const last = prev[prev.length - 1];
        const dist = getDistance(last.lat, last.lng, data.lat, data.lng);
        return dist > 5 ? [...prev, newPos] : prev;
      });

      clearTimeout(offlineTimer.current);
      offlineTimer.current = setTimeout(() => {
        setIsOnline(false);
        setStatus("Driver offline 🔴");
      }, 35000);
    });

    newSocket.on("planned_route", ({ route }) => {
      if (Array.isArray(route)) setPlannedRoute(route);
    });

    setSocket(newSocket);
    return () => {
      newSocket.disconnect();
      clearTimeout(offlineTimer.current);
    };
  }, [cust_bookingid]);

  useEffect(() => {
    if (!socket || !cust_bookingid) return;
    const interval = setInterval(() => {
      socket.emit("get_admin_driver_location", cust_bookingid);
    }, 30000);
    return () => clearInterval(interval);
  }, [socket, cust_bookingid]);

  if (!isLoaded) {
    return (
      <div className="d-flex justify-content-center align-items-center bg-light" style={{ height: "100vh" }}>
        <div className="text-center text-muted">
          <div className="spinner-border text-primary mb-3" style={{ width: "3rem", height: "3rem" }} />
          <div className="fw-bold fs-5 text-dark">Loading Maps API...</div>
          <div className="text-muted small">Please wait while we connect to satellite data</div>
        </div>
      </div>
    );
  }

  // --- UI STARTS HERE ---
  return (
    <div className="d-flex flex-column bg-light" style={{ height: "100vh", overflow: "hidden" }}>
      
      {/* ── Beautiful Header ── */}
      <div className="bg-white shadow-sm px-3 px-md-4 py-3 d-flex justify-content-between align-items-center" style={{ zIndex: 10 }}>
        <div className="d-flex align-items-center gap-3">
          <div
            className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
            style={{
              width: 42,
              height: 42,
              background: "linear-gradient(135deg, #1a56db, #3b82f6)",
              boxShadow: "0 4px 6px -1px rgba(59, 130, 246, 0.3)"
            }}
          >
            <i className="fa fa-location-arrow text-white fs-5"></i>
          </div>
          <div>
            <h4 className="mb-0 fw-bolder text-dark" style={{ letterSpacing: "-0.5px" }}>View Live Location</h4>
            <div className="text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
              Trip ID: <span className="text-primary">#{cust_bookingid || "Unknown"}</span>
            </div>
          </div>
        </div>

        <span 
          className={`badge rounded-pill px-3 py-2 d-flex align-items-center gap-2 shadow-sm ${
            isOnline ? "bg-success bg-opacity-10 text-success border border-success border-opacity-25" : "bg-danger bg-opacity-10 text-danger border border-danger border-opacity-25"
          }`} 
          style={{ fontSize: "0.75rem", letterSpacing: "0.5px" }}
        >
          <span style={{
            width: 8, height: 8, borderRadius: "50%",
            background: isOnline ? "#198754" : "#dc3545",
            animation: isOnline ? "pulse 1.4s infinite" : "none",
          }} />
          {isOnline ? "LIVE NOW" : "OFFLINE"}
        </span>
      </div>

      <div className="container-fluid flex-grow-1 d-flex flex-column p-2 p-md-3 gap-3" style={{ maxWidth: "1400px", margin: "0 auto" }}>
        
        {/* ── Modern Stat Widgets ── */}
        <div className="row g-2 g-md-3 flex-nowrap overflow-auto" style={{ scrollbarWidth: "none" }}>
          
          {/* Speed */}
          <div className="col-4 min-w-120">
            <div className="card border-0 shadow-sm h-100 p-2 p-md-3 text-center transition-all bg-white" style={{ borderRadius: "1rem" }}>
              <i className="fa fa-dashboard text-primary opacity-50 mb-1 mb-md-2" style={{ fontSize: "1.2rem" }}></i>
              <div className="fw-bolder text-dark lh-1" style={{ fontSize: "1.6rem" }}>{speed}</div>
              <div className="text-muted fw-bold mt-1" style={{ fontSize: "0.6rem", letterSpacing: "1px" }}>KM/H</div>
            </div>
          </div>
          
          {/* GPS Points */}
          <div className="col-4 min-w-120">
            <div className="card border-0 shadow-sm h-100 p-2 p-md-3 text-center transition-all bg-white" style={{ borderRadius: "1rem" }}>
              <i className="fa fa-map-marker text-success opacity-50 mb-1 mb-md-2" style={{ fontSize: "1.2rem" }}></i>
              <div className="fw-bolder text-dark lh-1" style={{ fontSize: "1.6rem" }}>
                {drivenPath.length > 0 ? drivenPath.length : "0"}
              </div>
              <div className="text-muted fw-bold mt-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", textTransform: "uppercase" }}>GPS Points</div>
            </div>
          </div>
          
          {/* Last Seen */}
          <div className="col-4 min-w-120">
            <div className="card border-0 shadow-sm h-100 p-2 p-md-3 text-center transition-all bg-white" style={{ borderRadius: "1rem" }}>
              <i className="fa fa-clock-o text-warning opacity-50 mb-1 mb-md-2" style={{ fontSize: "1.2rem" }}></i>
              <div className="fw-bolder text-dark text-truncate px-1 lh-1" style={{ fontSize: "1.1rem", marginTop: "0.25rem", marginBottom: "0.25rem" }}>
                {lastSeen || "—"}
              </div>
              <div className="text-muted fw-bold mt-1" style={{ fontSize: "0.6rem", letterSpacing: "1px", textTransform: "uppercase" }}>Last Seen</div>
            </div>
          </div>

        </div>

        {/* ── Map Container ── */}
        <div className="card shadow-sm border-0 position-relative flex-grow-1" style={{ borderRadius: "1.2rem", overflow: "hidden" }}>
          
          {/* Recenter Button (Sleek Dark Mode Style) */}
          {!isFollowing && currentPosition && (
            <button
              onClick={() => {
                setFollow(true);
                setMapCenter(currentPosition);
                if (mapRef.current) mapRef.current.setZoom(16);
              }}
              className="btn position-absolute fw-bold d-flex align-items-center gap-2"
              style={{
                top: 15, 
                left: "50%", 
                transform: "translateX(-50%)", 
                zIndex: 5, 
                borderRadius: "2rem", 
                padding: "10px 24px",
                backgroundColor: "#1f2937", // Modern dark gray
                color: "#ffffff",
                boxShadow: "0 10px 15px -3px rgba(0, 0, 0, 0.3)",
                border: "1px solid rgba(255,255,255,0.1)",
                fontSize: "0.9rem"
              }}
            >
              <i className="fa fa-crosshairs text-info"></i> Recenter Map
            </button>
          )}

          <GoogleMap
            mapContainerStyle={{ width: "100%", height: "100%", minHeight: "400px" }}
            center={mapCenter}
            zoom={16}
            onLoad={onMapLoad}
            onDragStart={() => setFollow(false)}
            options={{
              zoomControl: true,
              streetViewControl: false,
              mapTypeControl: false,
              fullscreenControl: true,
              gestureHandling: "greedy",
              styles: mapStyles,
            }}
          >
            {plannedRoute.length > 1 && (
              <Polyline
                path={plannedRoute}
                options={{ strokeColor: "#0d6efd", strokeOpacity: 0.5, strokeWeight: 6 }}
              />
            )}

            {drivenPath.length > 1 && (
              <Polyline
                path={drivenPath}
                options={{ strokeColor: "#198754", strokeOpacity: 1, strokeWeight: 5 }}
              />
            )}

            {currentPosition && (
              <Marker
                position={currentPosition}
                title="Driver is here"
                icon={
                  window.google && window.google.maps
                    ? {
                        path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                        scale: 6,
                        fillColor: isOnline ? "#1a73e8" : "#dc3545", 
                        fillOpacity: 1,
                        strokeColor: "#ffffff",
                        strokeWeight: 2,
                        rotation: heading,
                        anchor: new window.google.maps.Point(0, 2.5),
                      }
                    : undefined
                }
              />
            )}

            {drivenPath.length > 0 && (
              <Marker
                position={drivenPath[0]}
                title="Trip started here"
                icon={"http://maps.google.com/mapfiles/ms/icons/blue-dot.png"}
              />
            )}

            {plannedRoute.length > 0 && (
              <Marker
                position={plannedRoute[plannedRoute.length - 1]}
                title="Pickup point"
                icon={"http://maps.google.com/mapfiles/ms/icons/green-dot.png"}
              />
            )}
          </GoogleMap>
        </div>

        {/* ── Modern Legend ── */}
        <div className="card border-0 shadow-sm p-3 mb-1 bg-white" style={{ borderRadius: "1rem" }}>
          <div className="d-flex flex-wrap justify-content-around align-items-center gap-2">
            
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: 30, height: 6, background: "#0d6efd", opacity: 0.5, borderRadius: 3 }} />
              <div className="text-secondary fw-bold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Planned</div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div style={{ width: 30, height: 6, background: "#198754", borderRadius: 3 }} />
              <div className="text-secondary fw-bold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Driven</div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center justify-content-center">
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#0d6efd", border: "2px solid white", boxShadow: "0 0 4px rgba(0,0,0,.4)" }} />
              </div>
              <div className="text-secondary fw-bold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Start</div>
            </div>
            
            <div className="d-flex align-items-center gap-2">
              <div className="d-flex align-items-center justify-content-center">
                <div style={{ width: 14, height: 14, borderRadius: "50%", background: "#198754", border: "2px solid white", boxShadow: "0 0 4px rgba(0,0,0,.4)" }} />
              </div>
              <div className="text-secondary fw-bold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.5px" }}>Pickup</div>
            </div>

          </div>
        </div>

      </div>

      <style>{`
        @keyframes pulse {
          0%   { opacity: 1;   transform: scale(1);   }
          50%  { opacity: 0.4; transform: scale(1.4); }
          100% { opacity: 1;   transform: scale(1);   }
        }
        .min-w-120 {
          min-width: 120px;
        }
      `}</style>
    </div>
  );
}

export default LiveMapPage;